<script setup>
import { storeToRefs } from 'pinia';
import { computed, onMounted, ref } from 'vue';
import HawkTreeSelect from '~/common/components/organisms/hawk-tree/hawk-treeselect.vue';
import { useProjectManagementStore } from '~/project-management/store/pm.store';

const props = defineProps({
  initialActivityUid: {
    type: String,
    default: null,
  },
  disabledActivityUids: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['close', 'save']);

const $t = inject('$t');
const $toast = inject('$toast');

const project_management_store = useProjectManagementStore();
const { active_schedule_data } = storeToRefs(project_management_store);

const activities_tree = ref([]);
const is_loading = ref(false);
const expanded_items = ref([]);

const form_data = ref({
  activity_uid: props.initialActivityUid || null,
});

const initial_state = computed(() => {
  return props.initialActivityUid ? [props.initialActivityUid] : [];
});

function findParentUids(activityUid, activitiesData) {
  function findInTree(nodes, targetUid, currentPath = []) {
    for (const node of nodes) {
      const newPath = [...currentPath, node.uid];

      if (node.uid === targetUid) {
        return currentPath;
      }

      if (node.children && node.children.length > 0) {
        const result = findInTree(node.children, targetUid, newPath);
        if (result) {
          return result;
        }
      }
    }
    return null;
  }

  const parentPath = findInTree(activitiesData, activityUid);
  return parentPath || [];
}

function generateActivityHierarchy(activities) {
  const nodeMap = {};
  const result = [];

  activities.forEach((item) => {
    nodeMap[item.self_id] = {
      ...item,
    };
  });

  activities.forEach((item) => {
    const node = nodeMap[item.self_id];

    if (!item.parent_id) {
      result.push(node);
    }
    else {
      const parent = nodeMap[item.parent_id];
      if (parent) {
        if (!parent.children) {
          parent.children = [];
        }
        parent.children.push(node);
      }
    }
  });

  return result;
}

function getActivitiesData() {
  activities_tree.value = [];
  is_loading.value = true;

  const parsed_activities = active_schedule_data.value.data.map(activity => ({
    name: activity.name,
    id: activity.id,
    uid: activity.uid,
    self_id: activity.id,
    parent_id: activity.parent,
  }));

  activities_tree.value = generateActivityHierarchy(parsed_activities);

  if (props.initialActivityUid) {
    const parentUids = findParentUids(props.initialActivityUid, activities_tree.value);
    expanded_items.value = parentUids;
  }

  is_loading.value = false;
}

function handleSave() {
  if (!form_data.value.activity_uid) {
    emit('close');
    return;
  }

  if (props.disabledActivityUids.includes(form_data.value.activity_uid)) {
    $toast({
      title: $t('Activity already selected'),
      text: $t('This activity has already been mapped. Please select a different activity.'),
      type: 'warning',
    });
    return;
  }

  const findActivity = (activities, uid) => {
    for (const activity of activities) {
      if (activity.uid === uid) {
        return activity;
      }
      if (activity.children && activity.children.length > 0) {
        const found = findActivity(activity.children, uid);
        if (found)
          return found;
      }
    }
    return null;
  };

  const selected_activity = findActivity(activities_tree.value, form_data.value.activity_uid);

  emit('save', {
    activity_uid: form_data.value.activity_uid,
    activity_name: selected_activity?.name || '',
  });
}

function handleActivitySelection(event) {
  if (Array.isArray(event)) {
    form_data.value.activity_uid = event[0] || null;
  }
  else if (event) {
    form_data.value.activity_uid = event;
  }
}

onMounted(() => {
  getActivitiesData();
});
</script>

<template>
  <HawkModalTemplate @close="emit('close')">
    <template #header>
      <div class="flex items-center justify-between p-4">
        <div class="text-lg font-semibold">
          {{ $t('Select activity') }}
        </div>
        <div class="text-gray-600 rounded-md hover:bg-gray-50 cursor-pointer flex justify-center items-center p-2 ml-2" @click="emit('close')">
          <IconHawkXClose />
        </div>
      </div>
      <hr>
    </template>
    <div class="flex flex-col justify-between w-[900px] min-h-[300px]">
      <Vueform
        v-if="activities_tree.length && !is_loading"
        v-model="form_data"
        size="sm"
        class="w-full [&>*]:grid-cols-1"
      >
        <HawkTreeSelect
          :key="initial_state?.length"
          show_tree_select
          :show_expanded_search="true"
          :multi_select="false"
          :options="{
            name: 'activity_uid',
            placeholder: $t('Select the activity'),
            object: false,
            search: true,
            native: false,
          }"
          :data="activities_tree"
          :initial_state="initial_state"
          :initial_expanded_items="expanded_items"
          children_key="children"
          label_key="name"
          value_key="uid"
          :flat="true"
          class="flex flex-col justify-between w-[900px] min-h-[300px]"
          add_classes="!h-auto min-h-[240px] max-h-[420px] max-[1366px]:max-h-[320px] border border-gray-300 border-solid rounded-lg"
          @update-form="($event) => handleActivitySelection($event)"
        />
      </Vueform>
      <HawkLoader v-if="is_loading" />
      <div v-else-if="!activities_tree.length" class="w-full h-auto min-h-[240px] max-h-[420px] border border-gray-300 border-solid rounded-lg">
        <div class="text-sm font-medium text-center text-gray-700 pt-4">
          {{ $t('No data available') }}
        </div>
      </div>
    </div>
    <template #footer>
      <Vueform size="sm">
        <div class="flex justify-end w-full col-span-full">
          <ButtonElement
            name="cancel"
            class="mr-4"
            :secondary="true"
            @click="emit('close')"
          >
            {{ $t('Cancel') }}
          </ButtonElement>
          <ButtonElement
            name="save"
            :disabled="!form_data.activity_uid"
            @click="handleSave"
          >
            {{ $t('Save') }}
          </ButtonElement>
        </div>
      </Vueform>
    </template>
  </HawkModalTemplate>
</template>
